import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth'; 
import './PracticeTestInterface.css';

/* =============================================================
   UTILITIES (DB & PARSER) 
   Kept outside the component to prevent re-instantiation
============================================================= */
const DB_NAME = 'Context360TestDB';
const DB_STORE = 'assessments_v9';

const initDB = () => new Promise((res, rej) => {
    const r = indexedDB.open(DB_NAME, 9);
    r.onupgradeneeded = e => {
        const db = e.target.result;
        if (!db.objectStoreNames.contains(DB_STORE)) db.createObjectStore(DB_STORE);
    };
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
});

const dbSave = async (id, data) => {
    try {
        const db = await initDB();
        return new Promise((res, rej) => {
            const tx = db.transaction(DB_STORE, 'readwrite');
            tx.objectStore(DB_STORE).put(data, id);
            tx.oncomplete = res; tx.onerror = () => rej(tx.error);
        });
    } catch(e) { console.warn('IDB save failed:', e); }
};

const dbGet = async id => {
    try {
        const db = await initDB();
        return new Promise((res, rej) => {
            const tx = db.transaction(DB_STORE, 'readonly');
            const r = tx.objectStore(DB_STORE).get(id);
            r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error);
        });
    } catch(e) { return null; }
};

class QuizParser {
    static decodeHTML(html) {
        const t = document.createElement("textarea");
        t.innerHTML = html;
        return t.value;
    }
    static isValidQuiz(data) {
        if (!Array.isArray(data) || data.length === 0) return false;
        const sample = data[0];
        return (typeof sample === 'object' && sample !== null && Object.keys(sample).length > 0);
    }
    static normalize(arr) {
        return arr.map(q => {
            if (typeof q !== 'object' || q === null) return null;
            if (q.question !== undefined && q.options !== undefined && q.correctOption !== undefined) {
                return { ...q }; 
            }
            const keys = Object.keys(q);
            let question = '', options = [], correctOption = '', explanation = '';
            let usedKeys = new Set();
            const ignoreList = ['keyword', 'id', 'category', 'tags', 'meta'];
            const validKeys = keys.filter(k => !ignoreList.includes(k.toLowerCase()));

            const arrKey = validKeys.find(k => Array.isArray(q[k]));
            if (arrKey) { options = q[arrKey].map(String); usedKeys.add(arrKey); }

            const qKey = validKeys.find(k => /question|text|prompt|title|desc|q/i.test(k) && !usedKeys.has(k)) 
                      || validKeys.find(k => typeof q[k] === 'string' && q[k].includes('?') && !usedKeys.has(k))
                      || validKeys.find(k => typeof q[k] === 'string' && !usedKeys.has(k));
            if (qKey) { question = String(q[qKey]); usedKeys.add(qKey); }

            const ansKey = validKeys.find(k => /correct|answer|ans/i.test(k) && !usedKeys.has(k))
                        || validKeys.find(k => typeof q[k] === 'string' && options.includes(String(q[k])) && !usedKeys.has(k));
            if (ansKey) { correctOption = String(q[ansKey]); usedKeys.add(ansKey); }

            const expKey = validKeys.find(k => /exp|reason|rationale|sol|info|detail/i.test(k) && !usedKeys.has(k))
                        || validKeys.find(k => typeof q[k] === 'string' && !usedKeys.has(k));
            if (expKey) { explanation = String(q[expKey]); usedKeys.add(expKey); }

            if (options.length === 0) {
                validKeys.forEach(k => {
                    if (!usedKeys.has(k) && typeof q[k] === 'string') options.push(q[k]);
                });
            }
            return { 
                ...q, 
                question: question || q.question || '', 
                options: options.length ? options : (q.options || []), 
                correctOption: correctOption || q.correctOption || '', 
                explanation: explanation || q.explanation || '' 
            };
        }).filter(q => q && (q.question || (q.options && q.options.length > 0)));
    }
    static parse(rawRenderedHTML) {
        let s = this.decodeHTML(rawRenderedHTML);
        s = s.replace(/[\u201C\u201D\u201E\u201F\u00AB\u00BB]/g, '"')
             .replace(/[\u2018\u2019\u201A\u201B\u2032\u2035]/g, "'")
             .replace(/[\u200B-\u200D\uFEFF\u00AD]/g, '')
             .replace(/[\r\n\t]+/g, ' ')
             .replace(/([,\[{:])\s*<br\s*\/?>/gi, '$1 ')
             .replace(/<br\s*\/?>\s*([\]}:,])/gi, ' $1')
             .replace(/([\[\]{}])\s*<\/?p[^>]*>/gi, '$1 ')
             .replace(/<\/?p[^>]*>\s*([\[\]{}])/gi, ' $1');

        const start = s.indexOf('[');
        const end = s.lastIndexOf(']');
        if (start !== -1 && end !== -1 && end > start) {
            let jsonString = s.substring(start, end + 1);
            try {
                const parsed = new Function('return ' + jsonString)();
                if (this.isValidQuiz(parsed)) {
                    return { questions: this.normalize(parsed), method: 'Smart Structural Array Clean' };
                }
            } catch(e) {}
        }
        return this.extractObjectsFallback(s);
    }
    static extractObjectsFallback(str) {
        let objects = [], depth = 0, start = -1, inString = false, escapeNext = false;
        for (let i = 0; i < str.length; i++) {
            const char = str[i];
            if (escapeNext) { escapeNext = false; continue; }
            if (char === '\\') { escapeNext = true; continue; }
            if (char === '"') { inString = !inString; continue; }
            if (!inString) {
                if (char === '{') { if (depth === 0) start = i; depth++; } 
                else if (char === '}') {
                    depth--;
                    if (depth === 0 && start !== -1) objects.push(str.slice(start, i + 1));
                }
            }
        }
        const validQuestions = [];
        for (let objStr of objects) {
            try {
                const q = new Function('return ' + objStr)();
                const normalized = this.normalize([q])[0];
                if (normalized) validQuestions.push(normalized);
            } catch(e) {}
        }
        if (validQuestions.length > 0) return { questions: validQuestions, method: 'Fallback Object Extractor' };
        throw new Error("Data could not be parsed. Ensure your JSON format is correct.");
    }
}

// Render HTML utility
const renderHTML = (raw) => {
    if (!raw) return '';
    if (/<[a-z][\s\S]*>/i.test(raw)) return raw; 
    const safe = raw.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return safe.split(/\n\n+/).map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('');
};

/* =============================================================
   MAIN REACT COMPONENT
============================================================= */
export default function PracticeTestInterface() {
    const navigate = useNavigate();
    
    // Extract the dynamic parameter from the URL route (e.g., /users/practice/pinterface/12345)
    const { postId } = useParams(); 
    
    // Get the logged-in user
    const { user } = useAuth();
    
    // UI State
    const [loading, setLoading] = useState(true);
    const [loadStage, setLoadStage] = useState('Initializing Assessment Engine...');
    const [error, setError] = useState(null);
    const [title, setTitle] = useState('Assessment');
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(false);
    
    // Engine State
    const [questions, setQuestions] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [testState, setTestState] = useState([]); 
    const [scoreInfo, setScoreInfo] = useState({ score: 0, correct: 0, wrong: 0 });
    
    // Palette & Bookmarks
    const [bookmarks, setBookmarks] = useState([]);
    const [showOnlyBookmarks, setShowOnlyBookmarks] = useState(false);
    const [palPage, setPalPage] = useState(0);
    const palSize = 50;

    // Book Mode State
    const [bookModeOpen, setBookModeOpen] = useState(false);
    const [bmFullscreen, setBmFullscreen] = useState(false);
    const [bmShowAll, setBmShowAll] = useState(false);
    const [bmAnswersOnly, setBmAnswersOnly] = useState(false);
    const scrollAreaRef = useRef(null);

    // Initial Data Fetching
    useEffect(() => {
        if (!postId) {
            setError({ title: 'No Test ID', detail: 'Could not find a valid post ID in the URL.'});
            setLoading(false);
            return;
        }

        const loadData = async () => {
            try {
                // Check Cache
                setLoadStage('Checking local cache...');
                const cached = await dbGet(postId);
                if (cached?.questions?.length) {
                    initEngine(cached.questions, cached.title || 'Assessment');
                    return;
                }

                // Fetch from Blogger
                setLoadStage('Connecting to Blogger server...');
                const entry = await fetchBloggerPost(postId);
                if (!entry) throw new Error("Post data could not be retrieved.");
                
                const titleStr = entry.title?.$t || 'Assessment';
                setLoadStage('Extracting Question Blocks...');
                if (!entry.content || !entry.content.$t) throw new Error("No quiz content found.");

                const { questions: parsedQ } = QuizParser.parse(entry.content.$t);
                await dbSave(postId, { title: titleStr, questions: parsedQ, method: 'API' });
                initEngine(parsedQ, titleStr);
            } catch (err) {
                console.error(err);
                setError({ title: 'Quiz Data Parse Failed', detail: err.message });
                setLoading(false);
            }
        };

        const fetchBloggerPost = (id) => {
            return new Promise((resolve, reject) => {
                // 1. Extract ONLY the numeric part of the ID (fixes raw Blogger tag IDs)
                const cleanId = String(id).match(/\d+$/)?.[0];
                if (!cleanId) return reject(new Error('Invalid Post ID format'));

                // 2. Add a random string to the callback to make it strictly unique (Fixes React 18 Strict Mode double-firing)
                const uniqueId = Math.random().toString(36).substring(2, 9);
                const callbackName = `handleBloggerSinglePost_${cleanId}_${uniqueId}`;
                
                // 3. Proper cleanup function
                const cleanup = () => {
                    delete window[callbackName];
                    const scriptEl = document.getElementById(callbackName);
                    if (scriptEl) scriptEl.remove();
                };

                window[callbackName] = (data) => {
                    cleanup();
                    resolve(data.entry);
                };
                
                const script = document.createElement('script');
                script.id = callbackName;
                script.src = `https://context360.blogspot.com/feeds/posts/default/${cleanId}?alt=json-in-script&callback=${callbackName}`;
                
                script.onerror = () => {
                    cleanup();
                    reject(new Error('Failed to load post from Blogger. The test may be private or deleted.'));
                };
                
                document.head.appendChild(script);
            });
        };

        loadData();
    }, [postId]);

    // Engine Initializer
    const initEngine = (qs, testTitle) => {
        setTitle(testTitle);
        setQuestions(qs);
        document.title = `${testTitle} | GangaTricks`;
        
        // Load Bookmarks
        const storedBookmarks = JSON.parse(localStorage.getItem(`userdata_bookmarks_context360_${postId}`)) || [];
        setBookmarks(storedBookmarks);

        // Load Saved Index
        let startIdx = 0;
        const savedIndex = parseInt(localStorage.getItem(`cbt_index_${postId}`), 10);
        if (!isNaN(savedIndex) && savedIndex >= 0 && savedIndex < qs.length) {
            startIdx = savedIndex;
        }
        
        const initialTestState = Array(qs.length).fill(null);
        initialTestState[startIdx] = { visited: true, answered: false };

        setTestState(initialTestState);
        setCurrentIndex(startIdx);
        setPalPage(Math.floor(startIdx / palSize));
        setLoading(false);
    };

    // Derived Variables
    const currentQ = questions[currentIndex] || {};
    const currentState = testState[currentIndex] || {};
    const activeList = useMemo(() => {
        const full = Array.from({ length: questions.length }, (_, i) => i);
        return showOnlyBookmarks ? [...bookmarks].sort((a,b)=>a-b) : full;
    }, [questions.length, showOnlyBookmarks, bookmarks]);

    // Actions
    const markVisited = (idx) => {
        setTestState(prev => {
            const next = [...prev];
            if (!next[idx]) next[idx] = { visited: true, answered: false };
            return next;
        });
    };

    const jumpTo = (idx) => {
        if (idx >= 0 && idx < questions.length) {
            setCurrentIndex(idx);
            markVisited(idx);
            localStorage.setItem(`cbt_index_${postId}`, idx);
            if (scrollAreaRef.current) scrollAreaRef.current.scrollTop = 0;
            
            // Adjust Palette Page if needed
            const listIndex = activeList.indexOf(idx) === -1 ? 0 : activeList.indexOf(idx);
            const neededPage = Math.floor(listIndex / palSize);
            if (neededPage !== palPage) setPalPage(neededPage);
            
            if (window.innerWidth <= 900) setSidebarOpen(false);
        }
    };

    const handleNavigate = (dir) => {
        if (activeList.length === 0) return;
        if (dir === 'next') {
            const nextIdx = activeList.find(i => i > currentIndex);
            if (nextIdx !== undefined) jumpTo(nextIdx);
            else {
                alert(`Quiz complete!\nScore: ${scoreInfo.score.toFixed(2)}\nCorrect: ${scoreInfo.correct}  Wrong: ${scoreInfo.wrong}`);
            }
        } else {
            const prevIdx = [...activeList].reverse().find(i => i < currentIndex);
            if (prevIdx !== undefined) jumpTo(prevIdx);
        }
    };

    const handleAnswer = (selectedOpt) => {
        if (currentState.answered) return;
        const isCorrect = selectedOpt.trim() === currentQ.correctOption.trim();
        
        setTestState(prev => {
            const next = [...prev];
            next[currentIndex] = { visited: true, answered: true, selected: selectedOpt, isCorrect };
            return next;
        });

        setScoreInfo(prev => ({
            score: prev.score + (isCorrect ? 1 : -0.25),
            correct: prev.correct + (isCorrect ? 1 : 0),
            wrong: prev.wrong + (!isCorrect ? 1 : 0)
        }));
    };

    const handleSkip = () => {
        if (!currentState.answered) {
            setTestState(prev => {
                const next = [...prev];
                next[currentIndex] = { ...next[currentIndex], visited: true, skipped: true };
                return next;
            });
        }
        handleNavigate('next');
    };

    const toggleBookmark = (idx = currentIndex) => {
        setBookmarks(prev => {
            const next = prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx];
            localStorage.setItem(`userdata_bookmarks_context360_${postId}`, JSON.stringify(next));
            return next;
        });
    };

    // Palette Math
    const totalPalPages = Math.ceil(activeList.length / palSize);
    const palStart = palPage * palSize;
    const palEnd = Math.min(palStart + palSize, activeList.length);
    const currentPalItems = activeList.slice(palStart, palEnd);

    // Render Helpers
    const getLetters = ['A','B','C','D','E','F'];

    if (loading) {
        return (
            <div className="cbt-app-container">
                <div className="loader-screen">
                    {error ? (
                        <>
                            <svg width="48" height="48" fill="none" stroke="#ef4444" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                            </svg>
                            <strong style={{fontSize:'18px'}}>{error.title}</strong>
                            <p style={{fontSize:'14px', color:'#6b7280', maxWidth:'440px', lineHeight:1.7}}>{error.detail}</p>
                            <button className="btn btn-outline" onClick={() => navigate(-1)} style={{marginTop: '20px'}}>Go Back</button>
                        </>
                    ) : (
                        <>
                            <div className="loader-spinner"></div>
                            <div>Loading Assessment...</div>
                            <div className="loader-stage">{loadStage}</div>
                        </>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="cbt-app-container">
            {/* Header */}
            <header className="exam-header">
                <div className="header-brand">
                    <h1>GangaTricks</h1>
                    <span>{title}</span>
                </div>
                <button className="mobile-menu-btn" onClick={() => setSidebarOpen(true)}>
                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"/>
                    </svg>
                </button>
            </header>

            <div className="exam-layout">
                {/* Main Question View */}
                <div className="main-pane">
                    <div className="question-scroll-area" ref={scrollAreaRef}>
                        <div className="question-meta">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span>Question {currentIndex + 1} of {questions.length}</span>
                                <button title="Bookmark this question" onClick={() => toggleBookmark(currentIndex)} style={{ background:'none', border:'none', cursor:'pointer', padding: '4px', color: 'var(--text-muted)' }}>
                                    <svg width="20" height="20" fill={bookmarks.includes(currentIndex) ? 'var(--brand-primary)' : 'none'} stroke={bookmarks.includes(currentIndex) ? 'var(--brand-primary)' : 'currentColor'} strokeWidth="2" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/>
                                    </svg>
                                </button>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <label htmlFor="jumpInput" style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}>Jump to:</label>
                                <input 
                                    type="number" id="jumpInput" min="1" max={questions.length} 
                                    value={currentIndex + 1}
                                    onChange={(e) => {
                                        let val = parseInt(e.target.value, 10);
                                        if (!isNaN(val) && val >= 1 && val <= questions.length) jumpTo(val - 1);
                                    }}
                                    style={{ width: '60px', padding: '4px', textAlign: 'center', border: '1px solid var(--border-medium)', borderRadius: '4px' }} 
                                />
                            </div>
                        </div>

                        <div className="question-text rich-text" dangerouslySetInnerHTML={{ __html: renderHTML(currentQ.question) }} />

                        <div className="options-list">
                            {(currentQ.options || []).map((opt, i) => {
                                const isSelected = currentState.selected === opt;
                                const isCorrectOpt = opt === currentQ.correctOption;
                                
                                let cardClass = "option-card";
                                if (currentState.answered) {
                                    cardClass += " disabled";
                                    if (isCorrectOpt) cardClass += " is-correct";
                                    else if (isSelected && !currentState.isCorrect) cardClass += " is-wrong";
                                }

                                return (
                                    <label key={i} className={cardClass} onClick={() => handleAnswer(opt)}>
                                        <div className="opt-letter">{getLetters[i] || (i+1)}</div>
                                        <div className="opt-text rich-text" dangerouslySetInnerHTML={{ __html: renderHTML(opt) }} />
                                    </label>
                                );
                            })}
                        </div>

                        {currentState.answered && currentQ.explanation && (
                            <div className="solution-box">
                                <div className="solution-header">
                                    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                    </svg>
                                    Detailed Solution
                                </div>
                                <div className="solution-body rich-text" dangerouslySetInnerHTML={{ __html: renderHTML(currentQ.explanation) }} />
                            </div>
                        )}
                    </div>

                    <div className="action-footer">
                        <button className="btn btn-outline" onClick={() => handleNavigate('prev')} disabled={activeList.indexOf(currentIndex) <= 0}>« Prev</button>
                        
                        <div className="action-footer-right">
                            {!currentState.answered && (
                                <button className="btn-skip" onClick={handleSkip}>Skip →</button>
                            )}
                            <button className="btn btn-primary" onClick={() => handleNavigate('next')}>
                                {(activeList.indexOf(currentIndex) === activeList.length - 1) ? 'Finish' : 'Save & Next'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile Backdrop */}
                <div className={`mobile-backdrop ${sidebarOpen ? 'open' : ''}`} onClick={() => setSidebarOpen(false)} />

                {/* Sidebar Drawer */}
                <div className={`side-pane ${sidebarOpen ? 'open' : ''} ${desktopSidebarCollapsed ? 'collapsed-desktop' : ''}`}>
                    <div className="desktop-toggle-ribbon" onClick={() => setDesktopSidebarCollapsed(!desktopSidebarCollapsed)} title="Toggle Sidebar">
                        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                    </div>
                    
                    <div className="side-pane-content">
                        <div className="user-info">
                            <div className="user-details">
                                <img 
                                    src={user?.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.displayName || "Aspirant")}&background=e2e8f0&color=1e3a8a`} 
                                    alt="Profile" 
                                    className="avatar" 
                                    style={{ border: 'none', objectFit: 'cover' }}
                                />
                                <div>
                                    <h3 style={{ 
                                        fontSize: '15px', 
                                        margin: '0 0 4px 0', 
                                        color: '#0f172a', 
                                        fontWeight: 'bold',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: '120px'
                                    }}>
                                        {user?.displayName || 'Aspirant'}
                                    </h3>
                                    <div className="score-row">
                                        <span className="score-chip chip-score">Score: {Number.isInteger(scoreInfo.score) ? scoreInfo.score : scoreInfo.score.toFixed(2)}</span>
                                        <span className="score-chip chip-correct">✓ {scoreInfo.correct}</span>
                                        <span className="score-chip chip-wrong">✗ {scoreInfo.wrong}</span>
                                    </div>
                                </div>
                            </div>
                            <button className="mobile-menu-btn" style={{color:'var(--text-main)', display: sidebarOpen ? 'flex' : 'none'}} onClick={() => setSidebarOpen(false)}>
                                <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                                </svg>
                            </button>
                        </div>
                        
                        <div className="palette-scroll">
                            <div className="palette-header-row">
                                <h4 className="palette-title">Question Palette</h4>
                                <div className="palette-pagination">
                                    <button className="page-btn" disabled={palPage === 0} onClick={() => setPalPage(p => p - 1)}>◀</button>
                                    <span style={{ fontSize:'12px', fontWeight:600, minWidth:'60px', textAlign:'center' }}>
                                        {activeList.length === 0 ? '0-0' : `${palStart + 1}–${palEnd}`}
                                    </span>
                                    <button className="page-btn" disabled={palPage >= totalPalPages - 1} onClick={() => setPalPage(p => p + 1)}>▶</button>
                                </div>
                            </div>
                            
                            {activeList.length === 0 ? (
                                <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px', padding: '20px 0' }}>No bookmarked questions yet.</div>
                            ) : (
                                <div className="palette-grid">
                                    {currentPalItems.map(qIndex => {
                                        const s = testState[qIndex];
                                        let btnClass = "pal-btn";
                                        if (qIndex === currentIndex) btnClass += " current";
                                        if (s) {
                                            if (s.answered && s.isCorrect) btnClass += " correct";
                                            else if (s.answered && !s.isCorrect) btnClass += " wrong";
                                            else if (s.skipped || s.visited) btnClass += " unanswered";
                                        }
                                        return (
                                            <button key={qIndex} className={btnClass} onClick={() => jumpTo(qIndex)}>{qIndex + 1}</button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div className="legend-box">
                            <div className="legend-grid">
                                <div className="legend-item"><div className="lg-box lg-correct"></div>Correct</div>
                                <div className="legend-item"><div className="lg-box lg-wrong"></div>Incorrect</div>
                                <div className="legend-item"><div className="lg-box lg-skip"></div>Skipped</div>
                                <div className="legend-item"><div className="lg-box" style={{background:'#fff'}}></div>Not Visited</div>
                            </div>
                            
                            <button className="btn btn-outline" style={{width:'100%', marginBottom:'10px'}} onClick={() => navigate(-1)}>
                                Exit Assessment
                            </button>
                            
                            <button className={`btn ${showOnlyBookmarks ? 'btn-primary' : 'btn-outline'}`} style={{width:'100%', marginBottom:'10px'}} onClick={() => {
                                setShowOnlyBookmarks(!showOnlyBookmarks);
                                setPalPage(0);
                            }}>
                                {showOnlyBookmarks ? 'View All Questions' : 'View Bookmarked'}
                            </button>
                            
                            <button className="btn btn-outline" style={{width:'100%'}} onClick={() => setBookModeOpen(true)}>
                                📖 Open Book Mode
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Book Mode Modal */}
            {bookModeOpen && (
                <div className={`bookmode-viewer ${bmFullscreen ? 'is-fullscreen' : ''}`}>
                    <header className="postViewer-header">
                        <span>Book Mode</span>
                        <div className="postViewer-actions">
                            <button onClick={() => setBmFullscreen(!bmFullscreen)}>{bmFullscreen ? '⤡' : '⤢'}</button>
                            <button onClick={() => setBookModeOpen(false)}>✕</button>
                        </div>
                    </header>
                    <div className="postViewer-content">
                        <div style={{ marginBottom: '16px', display: 'flex', gap: '10px' }}>
                            <button className="btn btn-outline" style={{flex:1}} onClick={() => setBmShowAll(!bmShowAll)}>
                                {bmShowAll ? '🔖 Show Bookmarked Only' : '📋 Show All Questions'}
                            </button>
                            <button className="btn btn-outline" style={{flex:1}} onClick={() => setBmAnswersOnly(!bmAnswersOnly)}>
                                {bmAnswersOnly ? '📋 Show Full Questions' : '🎯 Show Only Answers'}
                            </button>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            {(bmShowAll ? questions.map((_, i) => i) : bookmarks).map((qIdx, mapIdx) => {
                                const q = questions[qIdx];
                                if (!q) return null;
                                
                                if (bmAnswersOnly) {
                                    return (
                                        <div key={qIdx} style={{ background: 'var(--status-correct-bg)', border: '1px solid var(--status-correct)', padding: '10px 14px', borderRadius: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                                            <span style={{ color: 'var(--text-secondary)', fontSize: '12px', fontWeight: 700 }}>Q{qIdx + 1}.</span> 
                                            <span style={{ fontWeight: 600, fontSize: '14px', color: '#065f46' }} dangerouslySetInnerHTML={{ __html: renderHTML(q.correctOption) }} />
                                        </div>
                                    );
                                }

                                return (
                                    <div key={qIdx} style={{ background:'#fff', border:'1px solid var(--border-medium)', borderRadius:'8px', padding: '16px', position:'relative' }}>
                                        <div style={{ position:'absolute', top:'14px', right:'14px', cursor:'pointer' }} onClick={() => toggleBookmark(qIdx)}>
                                            <svg width="22" height="22" fill={bookmarks.includes(qIdx) ? 'var(--brand-primary)' : 'none'} stroke={bookmarks.includes(qIdx) ? 'var(--brand-primary)' : 'var(--text-muted)'} strokeWidth="2" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/>
                                            </svg>
                                        </div>
                                        <div className="rich-text" style={{ fontWeight:600, marginBottom: '12px', paddingRight: '34px' }}>
                                            <span style={{ color:'var(--text-muted)', marginRight: '4px' }}>{mapIdx + 1}. Q{qIdx + 1}</span> 
                                            <span style={{ fontWeight:'normal' }} dangerouslySetInnerHTML={{ __html: renderHTML(q.question) }} />
                                        </div>
                                        <div className="rich-text" style={{ color: 'var(--status-correct)', background: 'var(--status-correct-bg)', padding: '10px 14px', borderRadius: '6px', border: '1px solid var(--status-correct)', fontSize: '15px' }}>
                                            <span style={{ marginRight: '6px' }}>✍</span> <b dangerouslySetInnerHTML={{ __html: renderHTML(q.correctOption || 'N/A') }} />
                                        </div>
                                        {q.explanation && (
                                            <details style={{ marginTop: '14px', background: '#f8fafc', border: '1px solid var(--border-light)', borderRadius: '6px' }}>
                                                <summary style={{ padding: '10px 14px', cursor: 'pointer', fontWeight: 600, color: 'var(--brand-primary)' }}>View Explanation</summary>
                                                <div className="rich-text" style={{ padding: '0 14px 14px 14px', fontSize: '14px' }} dangerouslySetInnerHTML={{ __html: renderHTML(q.explanation) }} />
                                            </details>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}